import { useMachine } from '@xstate/react'
import { useEffect } from 'react'

import { UserStatus } from '../../../api/_legacy/generated-api'
import { Counts, OperationName, UserState } from '../common/users.state'
import { UsersList } from '../users-list/users-list'
import { businessUnitUsersMachine } from './state/business-unit-users.state'

type BusinessUnitUsersStatelessProps = {
  businessUnitId: string
  counts: Counts
  filterStatus?: UserStatus
  notifyParentOfSave: () => void
  onActivate: () => void
  onDelete: () => void
  onEdit: (userName: string) => void
  onFilterByStatus: (status: 'All' | UserStatus) => void
  onSearch: (searchTerm: string) => void
  onUnlock: () => void
  operation: OperationName
  selectedUser?: UserState
  users: UserState[]
}

export function BusinessUnitUsersStateless({
  businessUnitId,
  counts,
  filterStatus,
  notifyParentOfSave,
  onActivate,
  onDelete,
  onEdit,
  onFilterByStatus,
  onSearch,
  onUnlock,
  operation,
  selectedUser,
  users
}: BusinessUnitUsersStatelessProps) {
  return (
    <UsersList
      addNewLinkText="Add a new business unit user"
      businessUnitId={businessUnitId}
      counts={counts}
      filterStatus={filterStatus}
      notifyParentOfSave={notifyParentOfSave}
      operation={operation}
      selectedUser={selectedUser}
      userType="business-unit"
      users={users}
      onActivate={onActivate}
      onDelete={onDelete}
      onEdit={onEdit}
      onFilterByStatus={onFilterByStatus}
      onSearch={onSearch}
      onUnlock={onUnlock}
    />
  )
}

export type Props = {
  businessUnitId: string
}

export function BusinessUnitUsers({ businessUnitId }: Props) {
  const [machine, send] = useMachine(businessUnitUsersMachine)

  useEffect(() => {
    send({ type: 'FETCH_USERS', businessUnitId })
  }, [businessUnitId, send])

  const { context } = machine

  return (
    <BusinessUnitUsersStateless
      businessUnitId={businessUnitId}
      counts={context.counts}
      filterStatus={context.filterStatus}
      notifyParentOfSave={() => send({ type: 'USER_SAVED' })}
      operation={context.operation}
      selectedUser={context.selectedUser}
      users={context.filteredUsers}
      onActivate={() => {
        send({ type: 'ACTIVATE' })
      }}
      onDelete={() => {
        send({ type: 'DELETE' })
      }}
      onEdit={userName => send({ type: 'SELECT_USER', userName })}
      onFilterByStatus={(status: UserStatus) => send({ type: 'FILTER_BY_STATUS', status })}
      onSearch={searchTerm => send({ type: 'SEARCH_USERS', searchTerm })}
      onUnlock={() => {
        send({ type: 'UNLOCK' })
      }}
    />
  )
}
